import { theme as mainTheme } from "./theme"

/**
 * Recreating structure of tailwind config from @autouncle/ui repo
 * but replacing values with our own theme
 *
 * Sizes are in rem with a base font size of 16px
 */
export const themeConfig = {
  container: {
    screens: {},
  },
  screens: {
    sm: "23.5rem", // 376px
    md: "48rem", // 768px
    lg: "64rem", // 1024px
    xl: "90rem", // 1440px
    hover: { raw: "(hover: hover)" },
    darkMode: { raw: "(prefers-color-scheme: dark)" },
    reducedMotion: { raw: "(prefers-reduced-motion: reduce)" },
  },
  containerSizes: {
    sm: "23.5rem", // 376px
    md: "35rem", // 560px
    lg: "90rem", // 1440px
  },
  colors: {
    white: "#fff",
    black: "#000",
    cool: {
      100: mainTheme.gray_100,
      200: mainTheme.gray_200,
      300: mainTheme.gray_300,
      400: mainTheme.gray_400,
      500: mainTheme.gray_500,
      600: mainTheme.gray_600,
      700: mainTheme.gray_700,
      800: mainTheme.gray_800,
      900: mainTheme.gray_900,
    },
    primary: {
      100: mainTheme.blue_100,
      200: mainTheme.blue_200,
      300: mainTheme.blue_300,
      400: mainTheme.blue_400,
      500: mainTheme.blue_500,
      700: mainTheme.blue_700,
      900: mainTheme.blue_900,
    },
    secondary: {
      100: mainTheme.variant_100,
      200: mainTheme.variant_200,
      300: mainTheme.variant_300,
      400: mainTheme.variant_400,
      500: mainTheme.variant_500,
      600: mainTheme.variant_600,
      700: mainTheme.variant_700,
      900: mainTheme.variant_900,
    },
    error: {
      100: mainTheme.red_100,
      200: mainTheme.red_200,
      300: mainTheme.red_300,
      500: mainTheme.red_500,
      700: mainTheme.red_700,
    },
    attention: {
      200: mainTheme.orange_200,
      300: mainTheme.orange_300,
      500: mainTheme.orange_500,
      600: mainTheme.orange_600,
      700: mainTheme.orange_700,
    },
    success: {
      200: mainTheme.green_200,
      300: mainTheme.green_300,
      500: mainTheme.green_500,
      700: mainTheme.green_700,
    },
    rating: {
      0: mainTheme.rating_0,
      1: mainTheme.rating_1,
      2: mainTheme.rating_2,
      3: mainTheme.rating_3,
      4: mainTheme.rating_4,
      5: mainTheme.rating_5,
    },
  },
  fontFamily: {
    title: [mainTheme.fontDisplay],
    body: [mainTheme.fontBody],
  },
  fontSize: {
    "2xs": ".625rem", // 10px
    xs: ".75rem", // 12px
    sm: ".875rem", // 14px
    base: "1rem", // 16px
    lg: "1.25rem", // 20px
    xl: "1.5rem", // 24px
    "2xl": "2rem", // 32px
    "3xl": "2.5rem", // 40px
    "4xl": "3rem", // 48px
  },
  lineHeight: {
    "2xs": "1rem", // 16px
    xs: "1.125rem", // 18px
    sm: "1.25rem", // 20px
    base: "1.375rem", // 22px
    lg: "1.75rem", // 28px
    xl: "2rem", // 32px
    "2xl": "2.5rem", // 40px
    "3xl": "3rem", // 48px
    "4xl": "3.75rem", // 60px
  },
  fontWeight: {
    regular: 400,
    medium: 500,
    semibold: 600,
  },
  spacing: {
    "3xs": ".125rem", // 1. 2px
    "2xs": ".25rem", // 2. 4px
    xs: ".5rem", // 3. 8px
    sm: ".75rem", // 4. 12px
    md: "1rem", // 5. 16px
    "2md": "1.25rem", // 6. 20px
    lg: "1.5rem", // 7. 24px
    xl: "2rem", // 8. 32px
    "2xl": "2.5rem", // 9. 40px
    10: "3rem", // 48px
    11: "3.5rem", // 56px
    12: "4rem", // 64px
    13: "5rem", // 80px
    14: "6rem", // 96px
    15: "7.5rem", // 120px
  },
  borderRadius: {
    sm: "0.25rem", // 4px
    md: "0.5rem", // 8px
    lg: "1rem", // 16px
  },
  boxShadow: {
    base: "0rem 0.125rem 0.0625rem rgba(0, 28, 73, 0.15)", // 0 2px 1px
    100: "0rem 0.125rem 0.25rem rgba(0, 28, 73, 0.15)", // 0 2px 4px
    300: "0rem 0.125rem 0.5rem rgba(0, 28, 73, 0.15)", // 0 2px 8px
  },
  lineWidth: {
    default: "0.0625rem", // 1px
  },
  transitionDuration: {
    0: "0ms",
    200: "200ms",
    400: "400ms",
    800: "800ms",
  },
}

/* Helper that returns the deeply nested theme configuration value */
export const tokensForTheme = (theme: typeof themeConfig = themeConfig) => {
  return (path: string): string | number | null =>
    // @ts-expect-error needs to be refactored to better reflect our
    // tokens structure
    path.split(".").reduce((p, c) => (p && p[c]) || null, theme)
}

export default tokensForTheme()
